import * as React from "react";
import { RouteContext } from "@swiggy-private/core-ui";
import { useNavigate } from "@checkout-core/hooks/UseNavigate";
import { CheckoutActionTypes } from "@checkout-core/reducers/Checkout";
import { CheckoutRoute } from "@swiggy-private/checkout-types";
import { CheckoutContext } from "@checkout-core/contexts/CheckoutContext";
import { HardwareBackContext } from "@checkout-core/contexts/HardwareBackContext";
import { useCallback } from "react";
import { isNotCartInitialRoute, navigateToCart } from "@checkout-core/helpers/Common";

export function RouteContainer(props: { children: React.ReactElement | null }): React.ReactElement | null {
    const { checkoutState } = React.useContext(CheckoutContext);
    const { setState: setHardwareBackState } = React.useContext(HardwareBackContext);
    const navigateTo = useNavigate();
    const { route } = React.useContext(RouteContext);
    const type = checkoutState.type;

    const redirectToPaymentPage = React.useMemo(() => {
        if (checkoutState.type === CheckoutActionTypes.PROCESS_ORDER) {
            return checkoutState.payload.webviewRefreshExtraParams?.redirectToPaymentPageWhenWebviewRefreshed;
        }
        return false;
    }, [checkoutState]);

    // TODO: Move to hooks
    const handleBack = useCallback(
        (event: PopStateEvent): void => {
            // if route is cart, close the webview
            if (route?.name === CheckoutRoute.CART) {
                history.pushState({ name: CheckoutRoute.CART, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on timer page
            if (route?.name === CheckoutRoute.VPA_TIMER) {
                history.pushState({ name: CheckoutRoute.VPA_TIMER, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle autopay payment page failures
            else if (route?.name === CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED) {
                history.pushState({ name: CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on add-swiggy-monet page and want to go back
            else if (route?.name === CheckoutRoute.ADD_SWIGGY_MONEY) {
                history.pushState({ name: CheckoutRoute.ADD_SWIGGY_MONEY, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route.name,
                });
                return;
            }

            // handle scenario when user is on home page and want to go back
            else if (route?.name === CheckoutRoute.PAYMENTS) {
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on swiggy payments page and want to go back
            else if (route?.name === CheckoutRoute.SWIGGY_MONEY_PAYMENTS) {
                history.pushState({ name: CheckoutRoute.SWIGGY_MONEY_PAYMENTS, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on penny txn status poller page and want to go back
            else if (route?.name === CheckoutRoute.PAYMENT_TXN_STATUS_POLLING) {
                history.pushState({ name: CheckoutRoute.PAYMENT_TXN_STATUS_POLLING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }
            // handle scenario when user on confirm order state do not navigate
            else if (route?.name === CheckoutRoute.CONFIRM_ORDER || route?.name === CheckoutRoute.DIRECT_PLACE_ORDER) {
                history.pushState({ name: route?.name, data: route?.data }, "", "");
                return;
            }
            // handle scenario when user has linked the wallet and want to go back
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            else if (route?.name === CheckoutRoute.VERIFY_WALLET_LINK_OTP && !!(route?.data as any)?.stopNavigation) {
                history.pushState({ name: route?.name, data: route?.data }, "", "");
                return;
            } else if (route?.name === CheckoutRoute.COUPON_LISTING) {
                history.pushState({ name: CheckoutRoute.COUPON_LISTING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            } else if (route?.name === CheckoutRoute.SHARED_CART) {
                history.pushState({ name: CheckoutRoute.SHARED_CART, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            } else if (route?.name === CheckoutRoute.GIFTING_CART) {
                history.pushState({ name: CheckoutRoute.GIFTING_CART, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            } else if (route?.name === CheckoutRoute.GIFTING_COUPON_LISTING) {
                history.pushState({ name: CheckoutRoute.GIFTING_COUPON_LISTING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            } else if (route?.name === CheckoutRoute.TOING_CART) {
                history.pushState({ name: CheckoutRoute.TOING_CART, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            if (event.state && event.state.name) {
                navigateTo(event.state.name, event.state.data);
            }
        },
        [route, navigateTo, setHardwareBackState],
    );

    React.useEffect(() => {
        window.addEventListener("popstate", handleBack);

        return (): void => {
            window.removeEventListener("popstate", handleBack);
        };
    }, [handleBack]);

    React.useEffect(() => {
        if (type === CheckoutActionTypes.SHOW_PAYMENTS) {
            if (
                [
                    CheckoutRoute.PAYMENTS,
                    CheckoutRoute.ADD_NEW_CARD,
                    CheckoutRoute.LINK_PAYLATER,
                    CheckoutRoute.LINK_PAYLATER_MOBILE,
                    CheckoutRoute.VERIFY_WALLET_LINK_OTP,
                    CheckoutRoute.LINK_WALLET_WITH_NUMBER,
                    CheckoutRoute.PAYMENTS_INDEX,
                    CheckoutRoute.MORE_BANKS,
                    CheckoutRoute.VERIFY_VPA,
                    CheckoutRoute.PAYMENT_TXN_STATUS_POLLING,
                    CheckoutRoute.MORE_PAYMENT_GROUPS,
                    CheckoutRoute.MORE_PAYMENT_METHODS,
                    CheckoutRoute.CART,
                    CheckoutRoute.TOING_CART,
                    CheckoutRoute.PAYLATER_LINKING_CONFIRMATION,
                    CheckoutRoute.PAYMENT_OFFERS,
                    CheckoutRoute.EMI_SELECTION,
                ].indexOf(route?.name as CheckoutRoute) === -1
            ) {
                navigateTo(CheckoutRoute.PAYMENTS);
                return;
            }
        }

        if (type == CheckoutActionTypes.SHOW_SWIGGYMONEY_PAYMENTS) {
            if (
                [
                    CheckoutRoute.SWIGGY_MONEY_PAYMENTS,
                    CheckoutRoute.ADD_SWIGGY_MONEY,
                    CheckoutRoute.ADD_NEW_CARD,
                    CheckoutRoute.PAYMENT_TXN_STATUS_POLLING,
                    CheckoutRoute.MORE_BANKS,
                    CheckoutRoute.MORE_PAYMENT_GROUPS,
                    CheckoutRoute.MORE_PAYMENT_METHODS,
                ].indexOf(route?.name as CheckoutRoute) === -1
            ) {
                navigateTo(CheckoutRoute.SWIGGY_MONEY_PAYMENTS);
            }
            return;
        }

        if (type === CheckoutActionTypes.SHOW_FULL_PAGE_LOADER) {
            route?.name !== CheckoutRoute.REDIRECT_PAGE && navigateTo(CheckoutRoute.REDIRECT_PAGE);
            return;
        }

        if (type === CheckoutActionTypes.POST_SWIGGY_ADD_MONEY || type === CheckoutActionTypes.POST_PAYLATER_LINKING) {
            route?.name !== CheckoutRoute.PAYMENTS && navigateTo(CheckoutRoute.PAYMENTS);
        }

        if (type === CheckoutActionTypes.DINEOUT_PAYMENT_SUCCESS) {
            route?.name !== CheckoutRoute.DINEOUT_PAYMENT_SUCCESS && navigateTo(CheckoutRoute.DINEOUT_PAYMENT_SUCCESS);
        }

        /** if Checkout state if vpa timer */
        if (type === CheckoutActionTypes.VPA_TIMER) {
            route?.name !== CheckoutRoute.VPA_TIMER && navigateTo(CheckoutRoute.VPA_TIMER);
            return;
        }

        /** if Checkout state is process order and webview refresh has happened then user would be redirected to payment page */
        if (redirectToPaymentPage) {
            route?.name !== CheckoutRoute.PAYMENTS && navigateTo(CheckoutRoute.PAYMENTS);
            return;
        }

        /** if Checkout state if upi intent poller */
        if (type === CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
            route?.name !== CheckoutRoute.PAYMENT_TXN_STATUS_POLLING &&
                navigateTo(CheckoutRoute.PAYMENT_TXN_STATUS_POLLING);
            return;
        }

        /** if Checkout state if confirm order */
        if (type === CheckoutActionTypes.CONFIRM_ORDER) {
            route?.name !== CheckoutRoute.CONFIRM_ORDER && navigateTo(CheckoutRoute.CONFIRM_ORDER);
        }

        /** if Checkout state if direct place order */
        if (type === CheckoutActionTypes.DIRECT_PLACE_ORDER) {
            route?.name !== CheckoutRoute.DIRECT_PLACE_ORDER && navigateTo(CheckoutRoute.DIRECT_PLACE_ORDER);
        }

        // For Autopay Phase 2, when payment fails both failure and success(tick screen)
        // are handled by same component.
        // Also we dont show tick screen in these cases.
        // This navigation is INTENTIONAL.
        if (
            type === CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED ||
            type === CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL
        ) {
            route?.name !== CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED &&
                navigateTo(CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED);
        }

        // Cart Routes
        if (type === CheckoutActionTypes.SHOW_CART) {
            // Handles initial cart route based on xChannel
            isNotCartInitialRoute(route) && navigateToCart(navigateTo);
            return;
        }

        if (type === CheckoutActionTypes.SHOW_COUPON_LISTING) {
            route?.name !== CheckoutRoute.COUPON_LISTING && navigateTo(CheckoutRoute.COUPON_LISTING);
            return;
        }

        // Group Cart
        if ([CheckoutActionTypes.SHARED_CART, CheckoutActionTypes.UNLOCK_GROUP_CART].indexOf(type) !== -1) {
            route?.name !== CheckoutRoute.SHARED_CART && navigateTo(CheckoutRoute.SHARED_CART);
            return;
        }

        // Gifting Cart
        if (type === CheckoutActionTypes.SHOW_GIFTING_CART) {
            route?.name !== CheckoutRoute.GIFTING_CART && navigateTo(CheckoutRoute.GIFTING_CART);
            return;
        }

        // Gifting Coupon Listing
        if (type === CheckoutActionTypes.SHOW_GIFTING_COUPON_LISTING) {
            route?.name !== CheckoutRoute.GIFTING_COUPON_LISTING && navigateTo(CheckoutRoute.GIFTING_COUPON_LISTING);
            return;
        }
    }, [type, navigateTo, route, redirectToPaymentPage]);

    return props.children;
}
